import React from "react";
import { Trans, useTranslation } from "react-i18next";

import {
  Box,
  Button,
  CompassColor,
  Heading,
  Image,
  Spacing,
  Stack,
  Text,
} from "@noom/wax-component-library";

import {
  QuestionComponentProps,
  QuestionDefinition,
} from "@/components/survey/Question";
import { InformationalScreen } from "@/components/survey/questionTypes/core";
import { useAppContext } from "@/contexts";

const QUESTION_ID = "scaleOrderConfirmation";

declare module "@/contexts" {
  interface SurveyAnswers {
    scaleOrderConfirmation?: boolean;
  }
}

const ScaleOrderConfirmation: React.FC<QuestionComponentProps> = ({
  onClickNext,
}) => {
  const { t } = useTranslation();
  const { eligibilityInfo } = useAppContext();
  const context =
    eligibilityInfo?.scaleEligible === "NOOMCOIN" ? undefined : "shipping";

  const bulletItems = [
    ...(context
      ? []
      : [
          {
            image: "/assets/img/log-weight.svg",
            text: t("scaleOrderConfirmation.bullets.logWeight"),
          },
        ]),
    {
      image: "/assets/img/log-food.svg",
      text: t("scaleOrderConfirmation.bullets.logMeals"),
      tip: t("scaleOrderConfirmation.bullets.logMealsTip"),
    },
    {
      image: "/assets/img/book-stack.svg",
      text: t("scaleOrderConfirmation.bullets.readArticles"),
    },
  ];

  return (
    <InformationalScreen questionId={QUESTION_ID}>
      <Stack spacing={Spacing[8]} alignItems="stretch">
        <Stack spacing={Spacing[4]}>
          <Heading color={CompassColor.white}>
            <Trans i18nKey="scaleOrderConfirmation.heading" context={context} />
          </Heading>
          <Text color={CompassColor.white}>
            <Trans i18nKey="scaleOrderConfirmation.body" context={context} />
          </Text>
        </Stack>
        <Stack spacing={Spacing[6]}>
          {bulletItems.map(({ image, text, tip }) => (
            <Box display="flex" key={image}>
              <Image src={image} alt="" height="40px" mr={Spacing[4]} />
              <Stack spacing={Spacing[0]}>
                <Text color={CompassColor.white}>{text}</Text>
                {tip && (
                  <Text color={CompassColor.white} fontSize="sm">
                    {tip}
                  </Text>
                )}
              </Stack>
            </Box>
          ))}
        </Stack>
        <Text color={CompassColor.white} mt={Spacing[4]}>
          <Trans i18nKey="scaleOrderConfirmation.footer" context={context} />
        </Text>
        <Stack spacing={Spacing[4]}>
          <Button
            colorScheme="primary"
            onClick={() => onClickNext(true)}
            size="xl"
          >
            {t("scaleOrderConfirmation.cta")}
          </Button>
        </Stack>
      </Stack>
    </InformationalScreen>
  );
};

export const Q_SCALE_ORDER_CONFIRMATION: QuestionDefinition = {
  id: QUESTION_ID,
  shouldShowQuestion: ({ surveyAnswers: { scaleOptIn, scaleEarningOptIn } }) =>
    scaleOptIn === true || scaleEarningOptIn === true,
  component: ScaleOrderConfirmation,
};
